import {noop} from "@firefly/fly-defter";
import {AuthenticationOptions, AuthenticationStatus} from "./typing";
import {Subscribable} from "./subscribable";

export interface AuthenticationState<
    TVariables = void,
    TPrincipal = unknown,
    IError = Error
> {
    principal: TPrincipal | undefined;
    variables: TVariables | undefined;
    error: IError | null;
    status: AuthenticationStatus;
}

interface InitialAction<TPrincipal> {
    type: 'initial';
    principal: TPrincipal | undefined;
}
interface PendingAction {
    type: 'pending';
}
interface SuccessAction<TPrincipal> {
    type: 'success';
    principal: TPrincipal | undefined;
}
interface ErrorAction<IError = Error> {
    type: 'error';
    error: IError;
}

export type Action<T, IError> =
    | InitialAction<T>
    | PendingAction
    | SuccessAction<T>
    | ErrorAction<IError>;

type AuthenticationObserverListener<TVariables, TPrincipal, IError> = (result: AuthenticationState<TVariables, TPrincipal, IError>) => void;

export function initialState<TVariables, TPrincipal, IError>(): AuthenticationState<TVariables, TPrincipal, IError> {
    return {
        principal: undefined,
        error: null,
        status: 'idle',
        variables: undefined,
    }
}
export class Authentication<TVariables = void, TPrincipal = unknown, IError = Error>
    extends Subscribable<AuthenticationObserverListener<TVariables, TPrincipal, IError>>
{
    state: AuthenticationState<TVariables, TPrincipal, IError>;
    _promise?: Promise<TPrincipal>;
    private options: AuthenticationOptions<TVariables, TPrincipal, IError>;
    constructor(options: AuthenticationOptions<TVariables, TPrincipal, IError>) {
        super();
        this.options = options;
        this.state = initialState();
    }

    get promise() {
        return this._promise;
    }

    cancel(): Promise<void> {
        const promise = this._promise;
        return promise ? promise.then(noop).catch(noop) : Promise.reject();
    }

    isPending() {
        return this.state.status === 'pending';
    }
    async execute(variables: TVariables) {
        const {
            onSettled,
            onSuccess,
            onError,
            authenticateFn
        } = this.options;
        // const restored = this.state.status === 'pending'
        // if (!restored) {
        //     this.dispatch({type: 'pending'})
        //     // const context = await this.options.onMutate?.(variables)
        // }
        if (this.isPending()) {
            if (this.promise) {
                return this.promise;
            }
        }

        // let promise = Promise.resolve();
        try {
            this.dispatch({
                type: 'pending',
            });

            const principal: TPrincipal = await authenticateFn!(variables);

            await onSuccess?.(principal);
            await onSettled?.(principal, null, variables);
            this.dispatch({
                type: 'success',
                principal: principal
            })
            return principal;
        } catch(reason) {
            const error = reason as IError;
            try {
                await onError?.(
                    error,
                    variables,
                )
                await onSettled?.(
                    undefined,
                    error,
                    variables
                )
                throw error;
            } finally {
                this.dispatch({
                    type: 'error',
                    error: error,
                })
            }
        }


    }
    reset() {

    }
    destroy() {

    }

    reducer = function(
        state: AuthenticationState<TVariables, TPrincipal, IError>,
        action: Action<TPrincipal, IError>,
    ): AuthenticationState<TVariables, TPrincipal, IError> {
        switch (action.type) {
            case 'initial': {
                return {
                    ...state,
                    status: 'idle',
                    error: null,
                    principal: action.principal,
                }
            }
            case 'pending': {
                return {
                    ...state,
                    status: 'pending',
                    error: null,
                    principal: undefined,
                };
            }
            case 'error': {
                const error = action.error;
                // if (isCancelledError(error)) {return {}}
                return {
                    ...state,
                    status: 'error',
                    error: error,
                };
            }
            case 'success': {
                const {
                    principal
                } = action;
                return {
                    ...state,
                    status: 'authenticated',
                    principal: principal,
                    error: null,
                };
            }
            // case 'invalidate': {
            //     return {
            //         ...state,
            //         isInvalidated: true,
            //         authenticated: undefined,
            //     };
            // }
        }
    }

    private dispatch(action: Action<TPrincipal, IError>): void {
        this.state = this.reducer(this.state, action);
        this.emit(this.state);
    }
}